import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Sparkles,
    TrendingUp,
    TrendingDown,
    Lightbulb,
    ChevronDown,
    ChevronUp,
} from 'lucide-react'
import { Button } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import {
    analyzeServiceBreakdownInsights,
    ServiceBreakdownInsightResponse,
} from '../../../../services/gemini'

interface ServiceData {
    name: string
    total: number
    renewal: number
    newSale: number
    upsell: number
    compTotal: number
    compRenewal: number
    compNewSale: number
    compUpsell: number
}

interface ServiceBreakdownAIInsightsProps {
    serviceData: ServiceData[]
    comparisonServiceData?: ServiceData[]
    periodRange: string
    comparisonPeriodRange?: string
    hasComparison: boolean
}

const ServiceBreakdownAIInsights = ({
    serviceData,
    comparisonServiceData,
    periodRange,
    comparisonPeriodRange,
    hasComparison,
}: ServiceBreakdownAIInsightsProps) => {
    const { t } = useTranslation()
    const [insights, setInsights] =
        useState<ServiceBreakdownInsightResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [expanded, setExpanded] = useState(true)
    const hasAutoAnalyzed = useRef(false)

    const handleAnalyze = async () => {
        if (serviceData.length === 0) return

        setLoading(true)
        setError(null)

        try {
            const result = await analyzeServiceBreakdownInsights({
                data: serviceData,
                period_range: periodRange,
                comparison_data: hasComparison
                    ? comparisonServiceData
                    : undefined,
                comparison_period_range: hasComparison
                    ? comparisonPeriodRange
                    : undefined,
            })
            setInsights(result)
        } catch (err) {
            setError('Không thể phân tích dữ liệu. Vui lòng thử lại.')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    // Auto analyze when data is available
    useEffect(() => {
        if (serviceData.length > 0 && !hasAutoAnalyzed.current && !insights) {
            hasAutoAnalyzed.current = true
            handleAnalyze()
        }
    }, [serviceData])

    if (!insights && !loading) {
        return (
            <Paper className="bg-gradient-to-r from-green-50 to-blue-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-900">
                            AI Service Insights
                        </span>
                        <span className="text-sm text-green-600">
                            Phân tích tăng trưởng dịch vụ
                        </span>
                    </div>
                    <Button
                        size="small"
                        onClick={handleAnalyze}
                        loading={loading}
                        disabled={serviceData.length === 0}
                        className="!bg-green-600 hover:!bg-green-700"
                    >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {t('common.analyze')}
                    </Button>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </Paper>
        )
    }

    if (loading) {
        return (
            <Paper className="bg-gradient-to-r from-green-50 to-blue-50">
                <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-green-600 animate-pulse" />
                    <span className="text-green-900">
                        Đang phân tích dữ liệu dịch vụ...
                    </span>
                </div>
            </Paper>
        )
    }

    if (!insights) return null

    return (
        <Paper className="bg-gradient-to-r from-green-50 to-blue-50">
            {/* Header */}
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900">
                        AI Service Insights
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAnalyze()
                        }}
                        loading={loading}
                        className="!text-green-600 !border-green-300"
                    >
                        {t('common.refresh')}
                    </Button>
                    {expanded ? (
                        <ChevronUp className="w-5 h-5 text-green-600" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-green-600" />
                    )}
                </div>
            </div>

            {/* Content */}
            {expanded && (
                <div className="mt-4 space-y-4">
                    {/* Summary */}
                    <div className="p-3 bg-white rounded-md">
                        <p className="text-sm text-gray-700">
                            {insights.summary}
                        </p>
                    </div>

                    {/* Top Growing Services */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-sm">
                                Dịch vụ tăng trưởng mạnh
                            </span>
                        </div>
                        <div className="space-y-2">
                            {insights.top_growing_services.map(
                                (service, index) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-white rounded-md"
                                    >
                                        <p className="text-sm font-medium text-green-600 mb-1">
                                            {service.name}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            {service.analysis}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>

                    {/* Declining Services */}
                    {insights.declining_services &&
                        insights.declining_services.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <TrendingDown className="w-4 h-4 text-red-600" />
                                    <span className="font-medium text-sm">
                                        Dịch vụ cần chú ý
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {insights.declining_services.map(
                                        (service, index) => (
                                            <div
                                                key={index}
                                                className="p-3 bg-white rounded-md"
                                            >
                                                <p className="text-sm font-medium text-red-600 mb-1">
                                                    {service.name}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {service.analysis}
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Growth Drivers */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">
                                Động lực tăng trưởng
                            </span>
                        </div>
                        <ul className="space-y-1.5">
                            {insights.growth_drivers.map((driver, index) => (
                                <li
                                    key={index}
                                    className="text-sm text-gray-600 flex items-start gap-1"
                                >
                                    <span className="text-blue-500 mt-0.5">
                                        •
                                    </span>
                                    {driver}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Comparison Insights */}
                    {insights.comparison_insights && (
                        <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                            <p className="text-sm font-medium text-blue-700 mb-1">
                                So sánh với kỳ trước
                            </p>
                            <p className="text-sm text-gray-600">
                                {insights.comparison_insights}
                            </p>
                        </div>
                    )}

                    {/* Recommendations */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-600" />
                            <span className="font-medium text-sm">Đề xuất</span>
                        </div>
                        <ul className="space-y-1.5">
                            {insights.recommendations.map((rec, index) => (
                                <li
                                    key={index}
                                    className="text-sm text-gray-600 flex items-start gap-1"
                                >
                                    <span className="text-yellow-500 mt-0.5">
                                        •
                                    </span>
                                    {rec}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </Paper>
    )
}

export default ServiceBreakdownAIInsights
